import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import BlueskyIcon from '~/components/Icons/BlueskyIcon';
import DiscordIcon from '~/components/Icons/DiscordIcon';
import FacebookIcon from '~/components/Icons/FacebookIcon';
import InstagramIcon from '~/components/Icons/InstagramIcon';
import LinkedinIcon from '~/components/Icons/LinkedInIcon';
import ThreadsIcon from '~/components/Icons/ThreadsIcon';
import TiktokIcon from '~/components/Icons/TiktokIcon';
import TwitterIcon from '~/components/Icons/TwitterIcon';
import YoutubeIcon from '~/components/Icons/YoutubeIcon';
import PublicationDetails from '~/components/PublicationHeading';
import Text from '~/components/Text';

import type { Publication } from '~/interfaces/publication';

import makeExternalUrl from '~/utils/makeExternalUrl';

interface HeaderProps {
  publication: Publication;
}

const PrivateHeader = ({ publication }: HeaderProps) => {
  const { t } = useTranslation();
  const backgroundColor = publication.web_theme.background_color;
  const textOnBackgroundColor = publication.web_theme.text_on_background_color;

  // Grab publication urls
  const tiktokUrl = publication.tiktok_url;
  const twitterUrl = publication.twitter_url;
  const youtubeUrl = publication.youtube_url;
  const facebookUrl = publication.facebook_url;
  const instagramUrl = publication.instagram_url;
  const linkedInUrl = publication.linkedin_url;
  const discordUrl = publication.discord_url;
  const threadsUrl = publication.threads_url;
  const blueskyUrl = publication.bluesky_url;
  const isAtLeastOnePresent =
    tiktokUrl ||
    twitterUrl ||
    youtubeUrl ||
    facebookUrl ||
    instagramUrl ||
    linkedInUrl ||
    discordUrl ||
    threadsUrl ||
    blueskyUrl;

  const renderSocialIcons = () => {
    return (
      <div className="flex flex-col space-y-1">
        {isAtLeastOnePresent ? (
          <Text as="span" theme="content" className="whitespace-nowrap text-xs font-semibold">
            {t('shared.cta.connect')}
          </Text>
        ) : (
          <div />
        )}
        <div className="flex space-x-2 transition-all">
          <div className="flex items-end space-x-2">
            {twitterUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(twitterUrl)} aria-label="X">
                <TwitterIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
            {linkedInUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(linkedInUrl)} aria-label="LinkedIn">
                <LinkedinIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
            {youtubeUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(youtubeUrl)} aria-label="Youtube">
                <YoutubeIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
            {tiktokUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(tiktokUrl)} aria-label="Tiktok">
                <TiktokIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
            {facebookUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(facebookUrl)} aria-label="Facebook">
                <FacebookIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
            {instagramUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(instagramUrl)} aria-label="Instagram">
                <InstagramIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
            {threadsUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(threadsUrl)} aria-label="Threads">
                <ThreadsIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
            {discordUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(discordUrl)} aria-label="Discord">
                <DiscordIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
            {blueskyUrl && (
              <a target="_blank" rel="noreferrer" href={makeExternalUrl(discordUrl)} aria-label="Discord">
                <BlueskyIcon className="h-4 w-4 fill-current opacity-40 hover:opacity-100" />
              </a>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={cx(
        'before:overflow-hidden-x relative mb-8 inline-block w-full bg-wt-background pt-8 before:absolute before:inset-0 before:block before:w-full before:bg-slate-100/50',
      )}
      style={{
        backgroundColor,
      }}
    >
      <div className="px-4">
        <div className="mx-auto flex w-full max-w-6xl flex-col pb-8 sm:pb-0">
          <div className="flex-start flex flex-col items-center py-2">
            <div className="z-10 mx-auto w-full max-w-2xl">
              <PublicationDetails publication={publication} />
            </div>

            <div className="hidden w-full items-end justify-between py-4 pt-8 sm:flex">
              <div className="text-xs font-light opacity-80" style={{ color: textOnBackgroundColor }}>
                {renderSocialIcons()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateHeader;
